import React, { useRef, useState } from "react";
import { Printer, BookOpen, Bell } from "lucide-react";
import { toast } from "sonner";
import { downloadElementsAsPdf } from "@/lib/pdf";
import { CARDS, SC_COLORS, LOOP, ROLES, MASTER_RULE } from "@/data/superCerveau";
import { IMG, Panel, Primary, Ghost, Field } from "./scShared";
import ScKitPdf from "./ScKitPdf";
import ScPartager from "./ScPartager";

const CardTile = ({ card, items }) => {
  const c = SC_COLORS[card.color];
  return (
    <div className="rounded-3xl overflow-hidden border-2 shadow-sm flex flex-col" style={{ borderColor: c.ring, background: c.bg }} data-testid={`sc-card-${card.id}`}>
      <img src={IMG(card.img)} alt="" className="w-full h-36 sm:h-40 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: c.fg }}>Carte</div>
        <h3 className="font-serif-title text-2xl leading-tight mb-1" style={{ color: c.fg }}>{card.title}</h3>
        <p className="text-sm text-[var(--ink)] font-medium mb-2">{card.sub}</p>
        <p className="text-xs text-[var(--ink-soft)] mb-3 flex-1">{items.slice(0, 6).join(" · ")}…</p>
        <p className="text-xs italic rounded-2xl bg-white/70 px-3 py-2" style={{ color: c.fg }}>🔎 « {card.question} »</p>
      </div>
    </div>
  );
};

export default function ScAccueil({ state, update, onStart, onGuide, onReminder }) {
  const { setup } = state;
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const [exporting, setExporting] = useState(false);
  const setSetup = (k, v) => update((s) => ({ ...s, setup: { ...s.setup, [k]: v } }));
  const start = () => { setSetup("started", true); onStart(); };
  const exportKit = async () => {
    setExporting(true);
    try { await downloadElementsAsPdf(refs.map((r) => r.current), "mission-super-cerveau-kit.pdf"); toast.success("Kit à imprimer exporté en PDF"); }
    catch { toast.error("Export PDF impossible"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-8" data-testid="sc-accueil">
      <div className="rounded-3xl overflow-hidden border border-[var(--border)] bg-white/80 shadow-sm">
        <img src={IMG("hero")} alt="" className="w-full h-56 sm:h-80 object-cover" data-testid="sc-hero-img" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <div className="p-6 sm:p-8 grid md:grid-cols-[1.4fr_1fr] gap-6">
          <div>
            <p className="text-lg text-[var(--ink)] leading-relaxed mb-3">
              <strong>Le but du jeu n'est pas d'être parfait.</strong> Le but est de découvrir comment fonctionne ton cerveau, ce qui l'aide, ce qui le gêne et quelles stratégies permettent de réussir les missions du quotidien.
            </p>
            <ul className="space-y-1.5 text-sm text-[var(--ink)] mb-5">
              {ROLES.map((r) => (
                <li key={r.r} className="flex gap-2"><span className="font-bold text-[var(--gold)] min-w-[9.5rem]">{r.r}</span><span>{r.w}</span></li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <Ghost onClick={exportKit} disabled={exporting} data-testid="sc-export-kit"><Printer size={16} /> {exporting ? "Préparation…" : "Kit à imprimer (PDF)"}</Ghost>
              <Ghost onClick={onGuide} data-testid="sc-open-guide"><BookOpen size={16} /> Guide parents : les 10 principes</Ghost>
              <Ghost onClick={onReminder} data-testid="sc-open-reminder"><Bell size={16} /> Rappel du soir</Ghost>
            </div>
          </div>
          <div className="rounded-3xl bg-[var(--sand)] p-5 space-y-3" data-testid="sc-setup">
            <h3 className="font-serif-title text-xl text-[var(--gold)]">L'équipe</h3>
            <Field label="Prénom du joueur (l'enfant)" value={setup.childName} onChange={(v) => setSetup("childName", v)} placeholder="ex. Léo" testid="sc-child-name" />
            <Field label="Prénom du coéquipier (le parent)" value={setup.parentName} onChange={(v) => setSetup("parentName", v)} placeholder="ex. Maman, Papa…" testid="sc-parent-name" />
            <Primary onClick={start} className="w-full" data-testid="sc-start">{setup.started ? "Reprendre la partie" : "Lancer la partie"}</Primary>
          </div>
        </div>
      </div>

      <section>
        <h2 className="font-serif-title text-2xl sm:text-3xl text-[var(--gold)] mb-1">1. Les 4 cartes du jeu</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-5">Chaque journée devient une petite enquête. L'enfant et son parent recherchent quatre types de cartes.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CARDS.map((c) => <CardTile key={c.id} card={c} items={state.lists[c.id]} />)}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <Panel title="La boucle du jeu" testid="sc-loop">
          <ol className="space-y-2">
            {LOOP.map((l, i) => (
              <li key={l.t} className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[var(--gold)] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div><div className="font-bold text-[var(--ink)] text-sm uppercase tracking-wide">{l.t}</div><div className="text-sm text-[var(--ink-soft)]">{l.d}</div></div>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-sm italic text-[var(--gold)] font-medium">« Le vrai niveau à débloquer n'est pas la perfection. C'est l'autonomie. »</p>
        </Panel>
        <Panel title="La règle du maître du jeu" testid="sc-master-rule">
          <p className="text-sm text-[var(--ink)] leading-relaxed mb-3">L'enfant participe aux choix, propose, discute et donne son avis. Mais le parent reste le maître du cadre. Il valide :</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {MASTER_RULE.map((m) => <span key={m} className="text-xs px-3 py-1.5 rounded-full bg-[var(--sand)] text-[var(--ink)]">{m}</span>)}
          </div>
          <p className="text-sm text-[var(--ink-soft)]">L'enfant a donc une voix, mais l'adulte conserve la responsabilité éducative. Le jeu, c'est <strong>enfant contre problème</strong> — jamais enfant contre parent.</p>
        </Panel>
      </div>
      <ScPartager childName={setup.childName} />
      <div className="fixed -left-[9999px] top-0" aria-hidden="true"><ScKitPdf state={state} refs={refs} /></div>
    </div>
  );
}
